import { queryParams } from "@iventis/utilities";
import { mapInitialState } from "../defaults/map-initial-state";
import { MapModuleLayer, MapState, Source, StoredBounds, StoredPosition } from "../types/store-schema";
import { createLocalObjects, calculateModelPlacement } from "./model-testing-helpers";
import { basicModelLayer } from "./sample-layers";

export const origin: [number, number] = [-0.132093, 51.503621];

export const modelLayerId = "model-layer-id";

const modelLayer: MapModuleLayer = {
    ...basicModelLayer,
    id: modelLayerId,
};

export const initialState: MapState = {
    ...mapInitialState,
    position: {
        value: { lng: origin[0], lat: origin[1], zoom: 15, bearing: 0, pitch: 0, source: Source.HOST },
        stamp: "",
    },
    globe: {
        value: false,
        stamp: "",
    },
    layers: {
        value: [modelLayer],
        stamp: "",
    },
    geoJSON: {
        value: {
            [modelLayerId]: createLocalObjects(modelLayerId, calculateModelPlacement(100, origin), parseInt(queryParams.level, 10) ?? 0),
        },
        stamp: "",
    },
};

export const startingTestPosition: StoredPosition = {
    lng: -0.145231526702446,
    lat: 51.501672389575305,
    zoom: 11.65,
    bearing: 0,
    pitch: 0,
    source: Source.HOST,
};

export const modelValues = [100, 400, 900, 1225, 20164, 40000, 60025] as const;

const transitionDuration = 10000;

export const cameraPositions: StoredBounds[] = [
    // Zoom and move to the back of the models
    {
        bounds: [
            [
                [-0.16266163558773883, 51.46807098872523],
                [-0.10498611625106946, 51.46807098872523],
                [-0.10498611625106946, 51.44408546356371],
                [-0.16266163558773883, 51.44408546356371],
                [-0.16266163558773883, 51.46807098872523],
            ],
        ],
        duration: transitionDuration,
        source: Source.HOST,
    },
    // Move to the front of the models
    {
        bounds: [
            [
                [-0.16029078556127274, 51.5612701523651],
                [-0.1024257085404372, 51.5612701523651],
                [-0.1024257085404372, 51.53725495303209],
                [-0.16029078556127274, 51.53725495303209],
                [-0.16029078556127274, 51.5612701523651],
            ],
        ],
        duration: transitionDuration,
        source: Source.HOST,
    },
    // Zoom to the front of the models
    {
        bounds: [
            [
                [-0.13921219882809055, 51.54086653604219],
                [-0.12712444895072395, 51.54086653604219],
                [-0.12712444895072395, 51.53584866822746],
                [-0.13921219882809055, 51.53584866822746],
                [-0.13921219882809055, 51.54086653604219],
            ],
        ],
        duration: transitionDuration,
        source: Source.HOST,
    },
    // Move to the middle of the models
    {
        bounds: [
            [
                [-0.14254686652634518, 51.49910748795247],
                [-0.12444531073941789, 51.49910748795247],
                [-0.12444531073941789, 51.491586069341935],
                [-0.14254686652634518, 51.491586069341935],
                [-0.14254686652634518, 51.49910748795247],
            ],
        ],
        duration: transitionDuration,
        source: Source.HOST,
    },
    // Move to the back of the models
    {
        bounds: [
            [
                [-0.1500724797250257, 51.46931727387448],
                [-0.12296517479083491, 51.46931727387448],
                [-0.12296517479083491, 51.45804603331524],
                [-0.1500724797250257, 51.45804603331524],
                [-0.1500724797250257, 51.46931727387448],
            ],
        ],
        duration: transitionDuration,
        source: Source.HOST,
    },
    // Zoom out to view all the models
    {
        bounds: [
            [
                [-0.2474041101876594, 51.54308218702869],
                [-0.04636322731897735, 51.54308218702869],
                [-0.04636322731897735, 51.45955821047011],
                [-0.2474041101876594, 51.45955821047011],
                [-0.2474041101876594, 51.54308218702869],
            ],
        ],
        duration: transitionDuration,
        source: Source.HOST,
    },
    // Zoom into the center of all the models
    {
        bounds: [
            [
                [-0.13592714990494414, 51.50407688037285],
                [-0.13103909850642026, 51.50407688037285],
                [-0.13103909850642026, 51.50204617878214],
                [-0.13592714990494414, 51.50204617878214],
                [-0.13592714990494414, 51.50407688037285],
            ],
        ],
        duration: transitionDuration,
        source: Source.HOST,
    },
    // Zoom out so the models are barely visible
    {
        bounds: [
            [
                [-0.7358321122131315, 51.712136237003676],
                [0.3431522070974893, 51.712136237003676],
                [0.3431522070974893, 51.2637368858976],
                [-0.7358321122131315, 51.2637368858976],
                [-0.7358321122131315, 51.712136237003676],
            ],
        ],
        duration: transitionDuration,
        source: Source.HOST,
    },
    // Move away from the models entirely so they are no longer in the viewport
    {
        bounds: [
            [
                [-3.2080195455596083, 51.64652808201083],
                [-2.1290352262489023, 51.64652808201083],
                [-2.1290352262489023, 51.19748061849259],
                [-3.2080195455596083, 51.19748061849259],
                [-3.2080195455596083, 51.64652808201083],
            ],
        ],
        duration: transitionDuration,
        source: Source.HOST,
    },
];

export interface ModelPerformanceData {
    id: string;
    testNumber: number;
    amountOfModels: number;
    averageFPS: string;
    below30FPS: string;
    below20FPS: string;
    below10FPS: string;
}
